/**
 * Human-friendly error extraction for axios / fetch responses.
 *
 * The legacy Casanéo API and FastAPI both surface Pydantic 422 validation
 * errors as `detail: [{msg, loc, type}, ...]`. Rendering that array directly
 * inside `toast.error(...)` produces the infamous `[object Object]` string.
 * Route every catch-block through {@link formatError} so users see the actual
 * cause instead.
 *
 * Usage:
 *   } catch (e) {
 *     toast.error(formatError(e, "Sauvegarde impossible"));
 *   }
 */

/** Flatten a Pydantic-style detail (str | array | object) to readable text. */
export function formatDetail(detail, fallback = "erreur") {
  if (detail == null || detail === "") return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((e) => {
        if (typeof e === "string") return e;
        const loc = Array.isArray(e?.loc)
          ? e.loc.filter((x) => x !== "body").join(".")
          : "";
        return [loc, e?.msg].filter(Boolean).join(" — ");
      })
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : fallback;
  }
  if (typeof detail === "object") {
    if (detail.msg) return detail.msg;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail);
}

/** Read `error.response.data.detail` (axios) or `error.message` and format. */
export function formatError(error, fallback = "Action impossible") {
  const detail = error?.response?.data?.detail;
  if (detail != null && detail !== "") return formatDetail(detail, fallback);
  if (error?.message) return error.message;
  return fallback;
}
